import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { Button } from '@/components/ui/button';
import { slugDeLaUrlActual, ultimoPortalActivo } from '@/lib/utils/portal-storage';

/**
 * Hace que la URL gane sobre la sesión.
 *
 * 🔴 Por qué. El `basename` sale del primer segmento de la URL, así que abrir
 * `/demo/quotes` con una sesión de Datec dejaría la URL diciendo una empresa y
 * la pantalla mostrando otra. Eso es peor que un error: es ver datos oficiales
 * creyendo que son de demostración.
 *
 * Con esto, dos pestañas con dos empresas distintas conviven de verdad, que es
 * la razón por la que el slug se puso en la ruta y no sólo en la puerta.
 */
export function PortalGuard({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const portalActivo = useAuthStore((s) => s.portalActivo);
  const portales = useAuthStore((s) => s.portales);
  const elegirPortal = useAuthStore((s) => s.elegirPortal);
  const refrescarPortales = useAuthStore((s) => s.refrescarPortales);

  const slugDeLaUrl = slugDeLaUrlActual();

  /*
    🔴 (20-oct) Arranca en «cambiando» cuando ya se sabe que hay que canjear.

    Al recargar una pantalla, `portalActivo` (no se persiste) vale `null`: arrancando en «ok», la aplicación se
    dibujaba —y pedía todos sus datos—, el efecto ponía «Entrando a…» desmontándola, y al volver el canje se
    dibujaba y pedía todo DE NUEVO. Cada recarga costaba el doble de pedidos (también en PROD: 2 por pantalla en
    vez de 1) y hacía chocar las E2E con el límite de 300 por minuto. Ahora se canjea primero y se dibuja una vez.
  */
  const [estado, setEstado] = useState<'ok' | 'cambiando' | 'sin-acceso' | 'error'>(() =>
    isAuthenticated && slugDeLaUrl && slugDeLaUrl !== portalActivo ? 'cambiando' : 'ok'
  );

  useEffect(() => {
    // Rutas públicas y sesiones cerradas no tienen empresa que reconciliar.
    if (!isAuthenticated || !slugDeLaUrl) return;

    /*
      🔴 Acá se pone «ok» a propósito, y no se sale sin tocar el estado.

      Cuando el canje sale BIEN escribe `portalActivo`, y eso re-renderiza. Como
      `portalActivo` está en las dependencias de este efecto, la limpieza del
      intento anterior marca `cancelado = true` — así que su
      `if (!cancelado) setEstado('ok')` ya no corre. Si esta rama saliera sin
      tocar el estado, quedaría en «cambiando» **para siempre**: la pantalla se
      queda en «Entrando a …» con la sesión perfectamente abierta.

      Le pasó a Sebastián en producción el 7-sep, entrando a
      `prixia.datec.com.bo/datec-srl`. Su salida fue borrar el segmento de la
      URL — y funcionaba, porque sin empresa en la URL este efecto sale por la
      línea de arriba y el estado nunca deja de ser «ok».
    */
    if (slugDeLaUrl === portalActivo) {
      setEstado('ok');
      return;
    }

    let cancelado = false;
    setEstado('cambiando');
    (async () => {
      try {
        await elegirPortal(slugDeLaUrl);
        if (!cancelado) setEstado('ok');
      } catch (err) {
        if (cancelado) return;
        // El canje distingue dos motivos DISTINTOS con el mismo "no se pudo":
        //  · 404 — ninguna empresa se llama `slugDeLaUrl`. No es que a esta
        //    persona le falte acceso: la URL es vieja (un bookmark de antes
        //    del prefijo, o el prefijo de una empresa que ya no existe).
        //  · 403 — la empresa existe, pero esta persona no tiene membresía.
        //    Ahí sí corresponde "no tenés acceso".
        // Cualquier otro fallo (red caída, 500, timeout) NO es ninguna de las
        // dos — antes se mostraba igual que un 403, y un blip transitorio le
        // decía a la persona que había perdido acceso a su propia empresa.
        const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
        if (status === 404) {
          /*
            Se antepone el prefijo correcto a la URL ENTERA, tal cual: `/quotes`
            (un bookmark de antes del prefijo de empresa) se vuelve
            `/<empresa>/quotes`, con el mismo query y el mismo hash. Esto
            resuelve bien el bookmark sin prefijo — el caso que afecta a
            CADA usuario existente.

            🔴 NO resuelve el prefijo de una empresa que ya no existe:
            `/empresa-vieja/quotes` se vuelve `/<empresa>/empresa-vieja/quotes`,
            un segmento de más que no matchea ninguna ruta y cae en la
            landing. Es a propósito — distinguir los dos casos exige la
            misma lista de rutas de la app que se decidió no mantener a mano
            (Important 7 del lote de revisión).
          */
          let listaActual = useAuthStore.getState().portales;
          if (listaActual.length === 0) {
            listaActual = await refrescarPortales()
              .then(() => useAuthStore.getState().portales)
              .catch(() => []);
          }
          /*
            El orden importa: primero lo que ya se VERIFICÓ contra el
            servidor, después el puntero guardado.

            · `portalActivo` — el canje de esta misma sesión lo puso.
            · `listaActual[0]` — acaba de volver de `/auth/portals`.
            · `ultimoPortalActivo()` — sobrevive la primera carga de una
              pestaña (donde `portalActivo`, que no se persiste, es `null`),
              pero puede nombrar una empresa que ya no existe.

            🔴 Con el puntero primero, un slug muerto ahí (una empresa
            borrada o renombrada por fuera) armaba un bucle infinito:
            `/quotes` → `/vieja/quotes` → 404 otra vez →
            `/vieja/vieja/quotes` → … cada salto una carga entera de página y
            un segmento más, sin salida más que borrar los datos del sitio —
            aunque `refrescarPortales()` ya hubiera devuelto una empresa viva.
          */
          const destino = portalActivo ?? listaActual[0]?.slug ?? ultimoPortalActivo();
          if (cancelado) return;
          if (destino) {
            window.location.href = `/${destino}${window.location.pathname}${window.location.search}${window.location.hash}`;
          } else {
            // No hay a dónde mandarla: ni empresa activa ni ninguna membresía.
            setEstado('sin-acceso');
          }
        } else if (status === 403) {
          await refrescarPortales().catch(() => undefined);
          if (!cancelado) setEstado('sin-acceso');
        } else if (!cancelado) {
          setEstado('error');
        }
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [isAuthenticated, slugDeLaUrl, portalActivo, elegirPortal, refrescarPortales]);

  if (estado === 'cambiando') {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-muted-foreground text-sm">Entrando a {slugDeLaUrl}…</span>
      </div>
    );
  }

  if (estado === 'error') {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 px-6">
        <h1 className="text-xl font-semibold">No se pudo entrar a «{slugDeLaUrl}»</h1>
        <p className="text-muted-foreground text-sm">
          Puede ser un problema de conexión. Probá de nuevo.
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (estado === 'sin-acceso') {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 px-6">
        <h1 className="text-xl font-semibold">No tenés acceso a «{slugDeLaUrl}»</h1>
        {portales.length > 0 ? (
          <>
            <p className="text-muted-foreground text-sm">Sí podés entrar a:</p>
            <div className="flex flex-col gap-2">
              {portales.map((p) => (
                <Button
                  key={p.slug}
                  variant="outline"
                  className="justify-start"
                  onClick={() => {
                    window.location.href = `/${p.slug}/dashboard`;
                  }}
                >
                  {p.name}
                </Button>
              ))}
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">
            No tienes ninguna empresa asignada. Pídele acceso a quien administra la plataforma.
          </p>
        )}
      </div>
    );
  }

  return <>{children}</>;
}
